/**
 * SHELL — boot orchestrator. Builds the App context, wires every subsystem, and
 * drives the daily flow. This is the conductor described in src/shell/index.ts.
 *
 * Responsibilities:
 *   - mount the Render canvas + UI layers into #app
 *   - derive today's day/seed, resolve the course, acquire the renderer
 *   - create anonymous-first auth, the network client, and the retry queue
 *   - run the fixed-timestep loop during a run, capture drag-to-steer
 *   - record results, optimistically advance the streak, submit (guarded)
 *   - route between non-game screens
 *
 * It depends ONLY on @/types, @/fixtures, and the locked @/core and @/render
 * surfaces — never on their internals.
 */

import { todaysSeed } from '@/types';
import type { CourseSpec, GameState, ReplayData } from '@/types';
import type { Simulation } from '@/core';

import type { App, SimulationFactory } from './app';
import { createStore } from './state/store';
import type { Phase } from './state/store';
import { createAuthClient } from './net/auth';
import { createNetworkClient } from './net/client';
import { createSubmitQueue } from './net/queue';
import {
  getStreak,
  hasPlayedToday,
  bumpLocalStreak,
  setServerStreak,
  setLastResult,
  getLastResult,
} from './storage/local';

import { injectStyles } from './ui/styles';
import { createRouter } from './ui/router';
import { acquireRenderer } from './game/renderer';
import { resolveCourse, defaultSimulationFactory } from './game/course';
import { setupInstallPrompts, registerServiceWorker } from './ui/install';

import { loadingScreen, errorScreen } from './ui/screens/loading';
import { landingScreen } from './ui/screens/landing';
import { runScreen } from './ui/screens/run';
import { resultsScreen } from './ui/screens/results';
import { leaderboardScreen } from './ui/screens/leaderboard';
import { streakScreen } from './ui/screens/streak';
import { claimScreen } from './ui/screens/claim';
import { practiceScreen } from './ui/screens/practice';

export interface BootOptions {
  /** Inject @/core's createSimulation here to go live with real physics. */
  readonly simulationFactory?: SimulationFactory;
}

/** Boot the whole experience into `root`. Resolves once the first screen mounts. */
export async function boot(root: HTMLElement, options: BootOptions = {}): Promise<void> {
  injectStyles();

  const { dateKey, seed } = todaysSeed();

  // --- DOM scaffold: canvas (Render) under the UI shell layer ---------------
  const canvas = document.createElement('canvas');
  canvas.className = 'dr-canvas';
  const shellLayer = document.createElement('div');
  shellLayer.className = 'dr-shell';
  root.replaceChildren(canvas, shellLayer);

  const router = createRouter(shellLayer);
  router.show(() => loadingScreen());

  // --- Renderer + course ----------------------------------------------------
  const { renderer } = acquireRenderer(canvas);
  let course: CourseSpec;
  try {
    course = resolveCourse(dateKey).course;
    renderer.buildCourse(course);
  } catch (err) {
    router.show(() =>
      errorScreen('We could not load today’s course. Please reload.', () => location.reload()),
    );
    throw err instanceof Error ? err : new Error('course build failed');
  }

  sizeCanvas(canvas, renderer);
  const onResize = (): void => sizeCanvas(canvas, renderer);
  window.addEventListener('resize', onResize);

  // --- Identity, network, queue --------------------------------------------
  const auth = await createAuthClient();
  const net = createNetworkClient({ auth });
  const queue = createSubmitQueue(net.submit.bind(net), (result) => {
    if (result.streak !== undefined) setServerStreak(result.streak, dateKey);
  });
  const stopQueue = queue.start();

  // --- Store ----------------------------------------------------------------
  const streak = getStreak();
  const lastResult = getLastResult();
  const playedToday = hasPlayedToday(dateKey, streak);
  const store = createStore({
    phase: 'landing',
    dateKey,
    seed,
    identity: auth.current(),
    streak,
    dailyStatus: playedToday ? 'completed' : 'unplayed',
    result:
      playedToday && lastResult?.dateKey === dateKey
        ? { metrics: lastResult.metrics, synced: lastResult.submitted }
        : null,
    courseReady: true,
    offline: auth.current().kind === 'offline',
  });

  auth.subscribe((identity) => store.set({ identity, offline: identity.kind === 'offline' }));

  const makeSimulation = options.simulationFactory ?? defaultSimulationFactory;

  // --- Run lifecycle state --------------------------------------------------
  let activeSim: Simulation | null = null;

  const app: App = {
    store,
    net,
    auth,
    queue,
    renderer,
    course,
    surface: canvas,
    makeSimulation,

    go(phase: Phase) {
      store.set({ phase });
      renderPhase(phase);
    },

    async startRun(opts = {}) {
      const practice = opts.practice === true;
      // Guard: only one scored attempt per day.
      if (!practice && store.get().dailyStatus === 'completed') {
        this.go('results');
        return;
      }
      try {
        activeSim = await makeSimulation(course);
        activeSim.reset();
      } catch (err) {
        router.show(() => errorScreen('The simulation failed to start. Please reload.', () => location.reload()));
        throw err instanceof Error ? err : new Error('sim init failed');
      }
      store.set({ phase: 'run', dailyStatus: practice ? store.get().dailyStatus : 'in_progress' });
      router.show(() => runScreen(app, { practice }));
    },

    activeSim() {
      if (!activeSim) throw new Error('No active simulation; startRun must be called first.');
      return activeSim;
    },

    sessionReplay() {
      return this.activeSim().getReplay();
    },

    async finishRun(_state: GameState, replay: ReplayData, practice: boolean) {
      if (practice) {
        // Practice never scores or affects the streak.
        store.set({ phase: 'landing' });
        renderPhase('landing');
        return;
      }

      // Optimistically advance the local streak (server overrides on sync).
      const newStreak = bumpLocalStreak(dateKey);
      setLastResult({ dateKey, metrics: replay.metrics, submitted: false });

      store.set({
        dailyStatus: 'completed',
        streak: newStreak,
        result: { metrics: replay.metrics, synced: false },
        phase: 'results',
      });
      renderPhase('results');

      // Fire-and-forget submit; the results screen re-renders if it syncs.
      void this.syncResult(replay);
    },

    async syncResult(replay: ReplayData) {
      const res = await net.submit(replay);
      if (!res.ok) {
        // Persist for automatic retry on reconnect / next boot.
        queue.enqueue(replay);
        return false;
      }
      const data = res.data;
      if (data.status === 'accepted' && data.streak !== undefined) {
        setServerStreak(data.streak, dateKey);
      }
      setLastResult({
        dateKey,
        metrics: replay.metrics,
        submitted: true,
        ...(data.rankGlobal !== undefined ? { rankGlobal: data.rankGlobal } : {}),
        ...(data.percentile !== undefined ? { percentile: data.percentile } : {}),
      });
      const cur = store.get();
      store.set({
        streak: getStreak(),
        result: cur.result ? { ...cur.result, submission: data, synced: true } : { metrics: replay.metrics, submission: data, synced: true },
      });
      // Re-render results if we're still there so rank/streak appear.
      if (store.get().phase === 'results') renderPhase('results');
      return true;
    },

    dispose() {
      window.removeEventListener('resize', onResize);
      stopQueue();
      router.dispose();
      renderer.dispose();
    },
  };

  function renderPhase(phase: Phase): void {
    switch (phase) {
      case 'loading':
        router.show(() => loadingScreen());
        break;
      case 'landing':
        router.show(() => landingScreen(app));
        break;
      case 'run':
        // startRun mounts the run screen itself; nothing to do here.
        break;
      case 'practice':
        router.show(() => practiceScreen(app));
        break;
      case 'results':
        router.show(() => resultsScreen(app));
        break;
      case 'leaderboard':
        router.show(() => leaderboardScreen(app));
        break;
      case 'streak':
        router.show(() => streakScreen(app));
        break;
      case 'claim':
        router.show(() => claimScreen(app));
        break;
    }
  }

  // --- First paint ----------------------------------------------------------
  renderPhase(store.get().phase);

  // --- PWA + install (best-effort, never blocks the game) -------------------
  setupInstallPrompts();
  void registerServiceWorker();

  // Expose for hot-reload teardown / debugging.
  (window as unknown as { __dailyroll?: App }).__dailyroll = app;
}

function sizeCanvas(canvas: HTMLCanvasElement, renderer: { resize(w: number, h: number): void }): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  try {
    renderer.resize(canvas.width, canvas.height);
  } catch {
    /* null renderer / not ready */
  }
}
